import React, { useEffect, useState, useCallback } from "react";
import styled, { keyframes } from "styled-components";
import { useNavigate } from "react-router-dom";
import i18n, { use } from 'i18next';
import { useTranslation } from "react-i18next";
import { ConfigProvider, Menu, Pagination, Tooltip, Select, Input } from 'antd';
import dayjs from "dayjs";

import FormHeader from "../components/form/FormHeader";
import FormTitle from "../components/form/FormTitle";
import FormInput from "../components/form/FormInput";
import FormSelect from "../components/form/FormSelect";
import FormSelect2 from "../components/form/FormSelect2";
import FormRemark from "../components/form/FormRemark";
import FormField from "../components/form/FormField";
import FormLabel from "../components/form/FormLabel";
import FormText from "../components/form/FormText";
import FormDatePicker from "../components/form/FormDatePicker";
import Flex from "../components/core/Flex";
import ShadowBox from "../components/core/ShadowBox";
import QuestionMark from "../components/form/QuestionMark";
import FormButtonDanger from "../components/form/FormButtonDanger";
import useHomeStore from "../store/useHomeStore";
import useViewStore from "../store/useViewStore";
import Footer from "../components/common/Footer";
import DropdownControl from "../components/core/DropdownControl";
// import AdminPage from "../components/admin/AdminPage";
import HeaderBar from "../components/admin/HeaderBar";
import useAdminApplicationStore from "../store/useAdminApplicationStore";
import useAdminApplicationDetailStore from "../store/useAdminApplicationDetailStore";
import useAuthAdminStore from "../store/useAuthAdminStore";
import useLoadingStore from "../store/useLoadingStore";
import withErrorHandling from "../function/withErrorHandling";
import Space from "../components/core/Space";
import useModalStore from "../store/useModalStore";
import { apiDomain, basename } from "../setting";

function PageAdminApplication() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { TextArea } = Input;
  const { isCSTeam } = useAuthAdminStore();
  const { firstName, lastName, role } = useAuthAdminStore(); //获得role
  const { NEWREQCount, SUPPLECount, REVIEWCount, APPROVEDCount, CLAIMCount, STPCount, AllCount, CANCELLEDCount } = useAdminApplicationStore();
  const { isSortingOpen, setIsSortingOpen, sort, setSort } = useAdminApplicationStore();
  const { selectedTab, setSelectedTab } = useAdminApplicationStore();
  const { searchText, setSearchText } = useAdminApplicationStore();
  const { page, recordperpage, ttlpage } = useAdminApplicationStore();
  const { setPage } = useAdminApplicationStore();
  const { applicationList, admListPreAuthForm, admUptPreAutFrmIntRmk, assignStaffToPreAuthForm, admListPreAuthFormToExcel } = useAdminApplicationStore();
  const { assignStaffList, getPreAuthStaff } = useAdminApplicationStore();
  const { filter_status, filter_flowType, filter_from_date, filter_to_date, filter_user } = useAdminApplicationStore();
  const { openModalConfirm } = useModalStore();
  const { internalRemark } = useAdminApplicationStore();
  const { isMobileView } = useViewStore();
  const { setLoading } = useLoadingStore();

  const items = [
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'NEWREQ'}>{t("translation:admin.request.tab.new_request")}</MenuItemText><MenuItemCount $active={selectedTab === 'NEWREQ'}>({NEWREQCount})</MenuItemCount></MenuItem>,
      key: 'NEWREQ',
    },
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'SUPPLE'}>{t("translation:admin.request.tab.pending_supple")}</MenuItemText><MenuItemCount $active={selectedTab === 'SUPPLE'}>({SUPPLECount})</MenuItemCount></MenuItem>,
      key: 'SUPPLE',
    },
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'REVIEW'}>{t("translation:admin.request.tab.pending_review")}</MenuItemText><MenuItemCount $active={selectedTab === 'REVIEW'}>({REVIEWCount})</MenuItemCount></MenuItem>,
      key: 'REVIEW',
    },
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'APPROVED'}>{t("translation:admin.request.tab.approved")}</MenuItemText><MenuItemCount $active={selectedTab === 'APPROVED'}>({APPROVEDCount})</MenuItemCount></MenuItem>,
      key: 'APPROVED',
    },
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'CLAIM'}>{t("translation:admin.request.tab.claim_service")}</MenuItemText><MenuItemCount $active={selectedTab === 'CLAIM'}>({CLAIMCount})</MenuItemCount></MenuItem>,
      key: 'CLAIM',
    },
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'STP'}>{t("translation:admin.request.tab.STP")}</MenuItemText><MenuItemCount $active={selectedTab === 'STP'}>({STPCount})</MenuItemCount></MenuItem>,
      key: 'STP',
    },
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'ALL'}>{t("translation:admin.request.tab.all")}</MenuItemText><MenuItemCount $active={selectedTab === 'ALL'}>({AllCount})</MenuItemCount></MenuItem>,
      key: 'ALL',
    },
    {
      label: <MenuItem><MenuItemText $active={selectedTab === 'CANCELLED'}>{t("translation:admin.request.tab.cancelled")}</MenuItemText><MenuItemCount $active={selectedTab === 'CANCELLED'}>({CANCELLEDCount})</MenuItemCount></MenuItem>,
      key: 'CANCELLED',
    },
  ];

  const onClick = (e) => {
    setSelectedTab(e.key);
    setPage(1);
  };

  const loadAdmListPreAuthForm = withErrorHandling(admListPreAuthForm);
  const loadGetPreAuthStaff = withErrorHandling(getPreAuthStaff);
  const loadAdmUptPreAutFrmIntRmk = withErrorHandling(admUptPreAutFrmIntRmk);
  const loadAssignStaffToPreAuthForm = withErrorHandling(assignStaffToPreAuthForm);

  useEffect(() => {
    loadAdmListPreAuthForm();
  }, [selectedTab, sort, page, recordperpage, filter_status, filter_flowType, filter_from_date, filter_to_date, filter_user]);

  useEffect(() => {
    const delay = setTimeout(() => {
      loadAdmListPreAuthForm();
    }, 300);

    return () => clearTimeout(delay);
  }, [searchText]);

  useEffect(() => {
    loadGetPreAuthStaff();
  }, []);

  const openFilter = () => {
    openModalConfirm({
      title: t("translation:admin.common.filter"),
      modalConfirmPrimaryButtonText: t("translation:admin.common.filter"),
      modalConfirmSecondaryButtonText: t("back"),
      content: <ContentFilter />,
      style: { content: { width: 720 } },
      callback: () => {
        loadAdmListPreAuthForm();
      },
    });
  }

  const ContentFilter = () => {
    const { intstatusList } = useAdminApplicationDetailStore();
    const { filter_status, setFilterStatus } = useAdminApplicationStore();
    const { flowTypeList, filter_flowType, setFilterFlowType } = useAdminApplicationStore();
    const { filter_user, setFilterUser } = useAdminApplicationStore();
    const { filter_from_date, setFilterFromDate } = useAdminApplicationStore();
    const { filter_to_date, setFilterToDate } = useAdminApplicationStore();

    return <Flex direction={"column"} alignItems={"flex-start"}>
      <Flex style={{ width: "100%" }} alignItems={"flex-start"} direction={isMobileView ? "column" : "row"} gap={isMobileView ? 5 : 20}>
        <FormField style={{ marginBottom: 8 }}>
          <FormLabel>{t("translation:admin.request.list_field.status")}</FormLabel>
          <FormSelect2
            placeholder={t("translation:admin.request.list_field.status")}
            size={"large"}
            allowClear
            options={[
              ...intstatusList.map((item) => {
                return {
                  label: item.label,
                  value: item.value,
                }
              })
            ]}
            value={filter_status}
            onChange={(value) => {
              setFilterStatus(value);
            }}
            style={{
              width: "100%",
            }}
          />
        </FormField>
        <FormField style={{ marginBottom: 8 }}>
          <FormLabel>{t("translation:admin.request.list_field.type")}</FormLabel>
          <FormSelect2
            placeholder={t("translation:admin.request.list_field.type")}
            size={"large"}
            allowClear
            options={flowTypeList}
            value={filter_flowType}
            onChange={(value) => {
              setFilterFlowType(value);
            }}
            style={{
              width: "100%",
            }}
          />
        </FormField>
      </Flex>
      <Flex style={{ width: "100%" }} alignItems={"flex-start"} direction={isMobileView ? "column" : "row"} gap={isMobileView ? 5 : 20}>
        <FormField style={{ marginBottom: 8 }}>
          <FormLabel>{t("translation:admin.request.list_field.submitted_date")}{i18n.language === "en" && " "}{t("from")}</FormLabel>
          <FormDatePicker
            value={filter_from_date ? dayjs(filter_from_date) : null}
            onChange={(date, dateString) => {
              setFilterFromDate(dateString);
            }}
          />
        </FormField>
        <FormField style={{ marginBottom: 8 }}>
          <FormLabel>{t("translation:admin.request.list_field.submitted_date")}{i18n.language === "en" && " "}{t("to")}</FormLabel>
          <FormDatePicker
            value={filter_to_date ? dayjs(filter_to_date) : null}
            onChange={(date, dateString) => {
              setFilterToDate(dateString);
            }}
          />
        </FormField>
      </Flex>
      <Flex style={{ width: "100%" }} alignItems={"flex-start"} direction={isMobileView ? "column" : "row"} gap={isMobileView ? 5 : 20}>
        <FormField style={{ marginBottom: 8 }}>
          <FormLabel>{t("translation:admin.request.list_field.assigned_to")}</FormLabel>
          <Select
            mode="multiple"
            placeholder={t("translation:admin.request.list_field.assigned_to")}
            size={"large"}
            allowClear
            options={[
              ...assignStaffList.map((item) => {
                return {
                  value: item.staffId,
                  label: `${item?.staffFirstName} ${item?.staffLastName} (${item?.role})`,
                }
              }),
            ]}
            value={filter_user}
            onChange={(value) => {
              setFilterUser(value);
            }}
            style={{
              width: "100%",
            }}
          />
        </FormField>
      </Flex>
    </Flex>
  }

  const ContentInternalRemark = (item) => {
    const { internalRemark, setInternalRemark } = useAdminApplicationStore();

    useEffect(() => {
      setInternalRemark(item?.intrmk);
    }, []);

    return (
      <Flex direction={"column"} gap={32}>
        <Flex direction={"column"} justifyContent={"space-between"} alignItems={"flex-start"} style={{ width: "100%" }} gap={8}>
          <Flex>
            <FormRemark style={{ width: 120 }}>{t("translation:admin.request.list_field.refno")}</FormRemark>
            <FormText>{item.refno}</FormText>
          </Flex>

          <Flex>
            <FormRemark style={{ width: 120 }}>{t("translation:admin.request.list_field.policy_no")}</FormRemark>
            <FormText>{item?.IDPLNO ?? "-"}</FormText>
          </Flex>

          <Flex>
            <FormRemark style={{ width: 120 }}>{t("translation:admin.request.list_field.insurred_no")}</FormRemark>
            <FormText>{item?.IDINSN ?? "-"}</FormText>
          </Flex>

          <Flex>
            <FormRemark style={{ width: 120 }}>{t("translation:admin.request.list_field.dependent_code")}</FormRemark>
            <FormText>{item?.DEPENDCODE || "-"}</FormText>
          </Flex>
        </Flex>

        <TextArea
          value={internalRemark}
          placeholder={t("translation:admin.request.list_field.internal_remark")}
          autoSize={{ minRows: 5 }}
          onChange={(e) => {
            setInternalRemark(e.target.value);
          }}
        />
      </Flex>
    )
  };

  return (
    <PageApplication>
      <HeaderBar title={t("translation:admin.portal_name")} name={`${firstName} ${lastName}`} role={role} />

      <PageContainer>
        <Flex>
          <FormHeader style={{ marginBottom: 0 }}>{t("translation:admin.request.list_title")}</FormHeader>
        </Flex>

        <MenuContainer>
          <ConfigProvider
            theme={{
              components: {
                Menu: {
                  itemBg: "transparent",
                  itemPaddingInline: 0,
                  horizontalItemHoverColor: "var(--aia-bx-blue-2)",
                  horizontalItemSelectedColor: "var(--aia-bx-blue-2)",
                  horizontalItemHoverBg: "var(--aia-bx-grey-20)",
                },
              },
            }}
          >
            <Menu onClick={onClick} selectedKeys={[selectedTab]} mode="horizontal" items={items} style={{ minWidth: 0, flex: "auto" }} />
          </ConfigProvider>

          <MenuExtend>
            <ExportBtn onClick={async () => {
              setLoading(true);
              const base64Result = await admListPreAuthFormToExcel();
              if (base64Result) {
                const link = document.createElement('a');
                link.href = base64Result;
                link.download = 'request.xlsx';
                link.click();
              }

              setLoading(false);
            }}>
              <Icon16 src={`${process.env.PUBLIC_URL}/images/document.png`} alt="export excel" />
              <ExportBtnText>{t("translation:admin.request.export")}</ExportBtnText>
            </ExportBtn>
          </MenuExtend>
        </MenuContainer>

        <ToolBar>
          <ToolBarLeft>
            <FormInput
              prefix={<Icon24 src={`${process.env.PUBLIC_URL}/images/magnifier.png`} alt="search" />}
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); }}
              placeholder={t("translation:admin.request.search")}
              style={{
                width: 300,
              }}
            />
          </ToolBarLeft>
          <ToolBarRight>
            <ToolBarItem onClick={() => {
              openFilter();
            }}>
              <Icon24 src={`${process.env.PUBLIC_URL}/images/slidebars.png`} alt={t("translation:admin.common.filter")} />
              <ToolBarItemText>{t("translation:admin.common.filter")}</ToolBarItemText>
            </ToolBarItem>
            <DropdownControl
              handleBlur={() => { setIsSortingOpen(false); }}
              placement={"right"}
              clickAndClose={true}
              open={isSortingOpen}
              // open={true}
              dropdown={
                <>
                  <FilterItem onClick={() => { setSort("submitdt desc"); }}>
                    <FormTitle>{t("translation:admin.request.list_field.submitted_date")} {t("translation:admin.common.sort.desc")}</FormTitle>
                  </FilterItem>
                  <FilterItem onClick={() => { setSort("submitdt asc"); }}>
                    <FormTitle>{t("translation:admin.request.list_field.submitted_date")} {t("translation:admin.common.sort.asc")}</FormTitle>
                  </FilterItem>
                  <FilterItem onClick={() => { setSort("moddt desc"); }}>
                    <FormTitle>{t("translation:admin.request.list_field.last_update")} {t("translation:admin.common.sort.desc")}</FormTitle>
                  </FilterItem>
                  <FilterItem onClick={() => { setSort("moddt asc"); }}>
                    <FormTitle>{t("translation:admin.request.list_field.last_update")} {t("translation:admin.common.sort.asc")}</FormTitle>
                  </FilterItem>
                </>
              }
            >
              <ToolBarItem onClick={() => { setIsSortingOpen(!isSortingOpen); }}>
                <Icon24 src={`${process.env.PUBLIC_URL}/images/chevron-ud.png`} alt={t("translation:admin.common.sort.title")} />
                <ToolBarItemText>{t("translation:admin.common.sort.title")}</ToolBarItemText>
              </ToolBarItem>
            </DropdownControl>
          </ToolBarRight>
        </ToolBar>

        <ItemList>
          {
            applicationList.map((item, index) => {
              return (
                <Item key={item.aid}>
                  {
                    item.redPoint > 0 && <PointRed />
                  }
                  <ItemLeft>
                    <Info>
                      <FormRemark>{t("translation:admin.request.list_field.internal_remark")}</FormRemark>
                      <Flex>
                        <Tooltip placement="top" title={item?.intrmk || ""}>
                          <FormText style={{ textOverflow: "ellipsis", whiteSpace: "nowrap", overflow: "hidden", width: 150 }}>
                            {item?.intrmk || "-"}
                          </FormText>
                        </Tooltip>

                        {
                          !isCSTeam() && <BtnEdit onClick={() => {
                            openModalConfirm({
                              title: t("translation:admin.request.list_field.internal_remark"),
                              content: <ContentInternalRemark {...item} />,
                              callback: async () => {
                                await loadAdmUptPreAutFrmIntRmk(item.aid, internalRemark);
                                await loadAdmListPreAuthForm();
                              },
                            })
                          }}>
                            <Icon24 src={`${process.env.PUBLIC_URL}/images/pen.svg`} alt="edit" />
                          </BtnEdit>
                        }
                      </Flex>
                    </Info>
                    <Info style={{ width: 100 }}>
                      <FormRemark>{t("translation:admin.request.list_field.type")}</FormRemark>
                      {item.flowtype === "LOG" && <FormText>{t("translation:admin.request.flow_type.LOG")}</FormText>}
                      {item.flowtype === "PCA" && <FormText>{t("translation:admin.request.flow_type.PCA")}</FormText>}
                      {item.flowtype === "STP" && <FormText>{t("translation:admin.request.flow_type.STP")}</FormText>}
                      {item.flowtype === "CHGTOPCA" && <FormText>{t("translation:admin.request.flow_type.CHGTOPCA")}</FormText>}
                      {item.flowtype === "-" && <FormText>-</FormText>}
                    </Info>
                    <Info style={{ width: 180 }}>
                      <FormRemark>{t("translation:admin.request.list_field.status")}</FormRemark>
                      <FormText style={{ fontWeight: "bold", color: "var(--aia-bx-dark-blue)" }}>{item.intstatusdesc}</FormText>
                    </Info>
                    <Info>
                      <FormRemark>{t("translation:admin.request.list_field.refno")}</FormRemark>
                      <Refno href={`/${basename}/${i18n.language}/admin/application/${item.aid}`}>{item.refno}</Refno>
                    </Info>
                    <Info>
                      <FormRemark>{t("translation:admin.request.list_field.insured_name")}</FormRemark>
                      <FormText>{item.fullname}</FormText>
                    </Info>
                    <Info>
                      <FormRemark>{t("translation:admin.request.list_field.policy_no")}</FormRemark>
                      <FormText>{item?.IDPLNO ?? "-"}</FormText>
                    </Info>
                    <Info>
                      <FormRemark>{t("translation:admin.request.list_field.insurred_no")}</FormRemark>
                      <FormText>{item?.IDINSN ?? "-"}</FormText>
                    </Info>
                    <Info>
                      <FormRemark>{t("translation:admin.request.list_field.dependent_code")}</FormRemark>
                      <FormText>{item?.DEPENDCODE || "-"}</FormText>
                    </Info>
                    <Info>
                      <FormRemark>{t("translation:admin.request.list_field.admission_date")}</FormRemark>
                      <FormText>{item?.admissiondt?.substr(0, 10) ?? "-"}</FormText>
                    </Info>
                    <Info>
                      <FormRemark>{t("translation:admin.request.list_field.hospital")}</FormRemark>
                      <FormText>{item?.hospital ?? "-"}</FormText>
                    </Info>
                    <Info>
                      <FormRemark>{t("translation:admin.request.list_field.submitted_date")}</FormRemark>
                      <FormText>{item?.submitdt?.substr(0, 19)?.replace("T", " ") ?? "-"}</FormText>
                    </Info>
                    <Info>
                      <FormRemark>{t("translation:admin.request.list_field.last_update")}</FormRemark>
                      <FormText>{item?.moddt?.substr(0, 10) ?? "-"}</FormText>
                    </Info>
                    <Info>
                      <FormRemark>{t("translation:admin.request.list_field.request_info")}</FormRemark>
                      <FormText>
                        <Tooltip placement="left" title={item.oprtn}>
                          <View>{t("translation:admin.request.list_field.view")}</View>
                        </Tooltip>
                      </FormText>
                    </Info>
                    <Info>
                      <FormRemark>{t("translation:admin.request.list_field.assigned_to")}</FormRemark>
                      <Select
                        showSearch
                        placeholder={t("translation:admin.request.list_field.assign")}
                        optionFilterProp="label"
                        style={{ width: 200 }}
                        disabled={isCSTeam()}
                        value={item.handleStaff === "" ? null : item.handleStaff}
                        onChange={async (data) => {
                          await loadAssignStaffToPreAuthForm(item.aid, data ?? "");
                          await loadAdmListPreAuthForm();
                        }}
                        allowClear={true}
                        options={assignStaffList.map((item) => {
                          return {
                            value: item.staffId,
                            label: `${item?.staffFirstName} ${item?.staffLastName} (${item?.role})`,
                          }
                        })}
                      />
                    </Info>
                  </ItemLeft>
                  <ItemRight>
                    <DetailBtn href={`/${basename}/${i18n.language}/admin/application/${item.aid}`}>
                      <DetailImg src={`${process.env.PUBLIC_URL}/images/chevron-em-r.png`} alt="detail" />
                    </DetailBtn>
                  </ItemRight>
                </Item>
              );
            })
          }
        </ItemList>

        {
          (page && recordperpage && ttlpage > 0) &&
          <PaginationContainer>
            <Pagination current={page} total={recordperpage * ttlpage} showSizeChanger={false} onChange={(page, pageSize) => {
              setPage(page)
            }} />
          </PaginationContainer>
        }
      </PageContainer>
    </PageApplication>
  );
}

const PageApplication = styled(Flex)`
  width: 100%;
  height: 100%;
  background-color: var(--aia-bx-gray-10);
  flex-direction: column;
  align-items: flex-start;
  overflow-y: auto;
  gap: 0px;
`;

const PageContainer = styled(Flex)`
  padding: 24px 36px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
`;

const MenuContainer = styled(Flex)`
  width: 100%;
  align-items: flex-end;
  gap: 0;
`;

const MenuItem = styled.div`
  display: flex;
  margin: 0px 24px;
  flex-direction: row;
  gap: 8px;
`;

const MenuItemText = styled.div`
  font-size: 14px;
  color: var(--aia-bx-dark-blue);
  text-shadow: 0 0 0 transparent;
  font-weight: normal;
  transition: font-weight 0.3s ease;
  ${(props) => props.$active && `font-weight: bold;`}
`;

const MenuItemCount = styled.div`
  color: var(--aia-bx-blue-2);
  text-shadow: 0 0 0 transparent;
  font-weight: normal;
  transition: font-weight 0.3s ease;
  ${(props) => props.$active && `font-weight: bold;`}
`;

const MenuExtend = styled.div`
  line-height: 46px;
  border: 0;
  border-bottom: 1px solid rgba(5, 5, 5, 0.06);
  box-shadow: none;
`;

const ExportBtn = styled.button`
  border: none;
  background-color: var(--aia-bx-blue-2);
  cursor: pointer;
  padding: 6px 24px;
  border-radius: 50px;
  display: flex;
  flex-direction: row;
  gap: 8px;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  
  &:hover {
    background-color: var(--aia-bx-blue-6);
  }
`;

const ExportBtnText = styled.div`
  font-size: 14px;
  line-height: 24px;
  color: var(--aia-bx-white);
`;

const ToolBar = styled(ShadowBox)`
  border-radius: 50px;
  padding: 8px;
  justify-content: space-between;
  width: fit-content;
  min-width: 100%;
`;

const ToolBarLeft = styled(Flex)`
  gap:8px;
`;

const ToolBarRight = styled(Flex)`
  gap: 8px;
  padding-right: 24px;
`;

const FilterItem = styled.button`
  border: none;
  background: none;
  display: flex;
  justify-content: space-between;
  padding: 16px;
  gap: 16px;
  cursor: pointer;
  border-radius: 5px;
  width: 100%;
  min-width: 230px;

  &:hover {
    background-color: var(--aia-bx-bright-blue-10);
  }
`;

const Icon24 = styled.img`
  width: 24px;
  height: 24px;
`;

const Icon16 = styled.img`
  width: 16px;
  height: 16px;
`;

const CheckboxImg = styled.img`
  width: 24px;
  height: 24px;
`;

const ToolBarItem = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  display: flex;
  flex-direction: row;
  gap: 8px;
  border-radius: 5px;

  &:hover {
    background-color: var(--aia-bx-bright-blue-10);
  }
`;

const ToolBarItemText = styled.div`
  font-size: 16px;
  line-height: 24px;
  color: var(--aia-bx-dark-blue);
`;

const ItemList = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  height: calc(100vh - 416px);
  overflow: auto;
  padding: 5px;
  margin: -5px;
`;

const fadeIn = keyframes`
  from {
    transition: opacity 0.5s ease-in-out;
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Item = styled(ShadowBox)`
  padding: 0px;
  justify-content: space-between;
  width: max-content;
  min-width: 100%;
  animation: ${fadeIn} 0.5s ease-out;
  position: relative;
`;

const ItemLeft = styled(Flex)`
  padding: 16px 32px;
  gap: 40px;
  flex-shrink: 0;
`;

const Info = styled(Flex)`
  flex-direction: column;
  gap: 8px;
  align-items: flex-start;
  min-width: 80px;
`;

const ItemRight = styled(Flex)`
  height: 100%;
`;

const DetailBtn = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 88px;
  height: 100%;
  background: none;
  border: none;
  cursor: pointer;
  border-top-right-radius: 10px;
  border-bottom-right-radius: 10px;
  border-left: 1px solid var(--aia-bx-grey-20);

  &:hover {
    background-color: var(--aia-bx-bright-blue-10);
  }
`;

const DetailImg = styled.img`
  width: 41px;
  height: 32px;
`;

const PaginationContainer = styled(ShadowBox)`
  width: auto;
  align-self: center;
`;

const View = styled.div`
  color: var(--aia-bx-blue-2);
  cursor: pointer;
  text-decoration: underline;
`;

const PointRed = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--aia-bx-aiaRed-60);
  position: absolute;
  top: -3px;
  left: -3px;
`;

const Refno = styled.a`
  font-size: 14px;
  line-height: 24px;
  color: var(--aia-bx-blue-2);
  font-weight: 400;
`;

const BtnEdit = styled.button`
  border: none;
  background-color: transparent;
  cursor: pointer;
`;

export default PageAdminApplication;